import apiClient from './api';
import { ConfigDocument, S3TestParams, SMBTestParams, TestResult, AuditEntry } from '../types';

/**
 * Parameters for testing a SharePoint connection.
 */
export interface SharePointTestParams {
  credentialSetName: string;
  siteUrl: string;
  documentLibrary: string;
}

/**
 * Config API Service Module
 *
 * Handles all HTTP communication for the Configuration page.
 * Uses the shared apiClient (auth interceptor, base URL, error handling).
 *
 * Requirements: 10.2, 10.3, 5.5
 */

/**
 * Custom error for 409 Conflict (optimistic concurrency failure).
 * Thrown when a PUT /config request fails because the config was
 * modified by another user since it was last loaded.
 */
export class ConflictError extends Error {
  constructor(message: string = 'Configuration was modified by another user. Please reload and try again.') {
    super(message);
    this.name = 'ConflictError';
  }
}

/**
 * Get the current configuration document.
 *
 * @returns The current ConfigDocument from the backend
 */
export async function getConfig(): Promise<ConfigDocument> {
  const response = await apiClient.get<ConfigDocument>('/config');
  const data = response.data;
  // Ensure digitalDestinations exists for configs created before this field was added
  if (!data.digitalDestinations) {
    data.digitalDestinations = {};
  }
  return data;
}

/**
 * Update the configuration document with optimistic concurrency.
 * The config must include the current `version` field — the backend
 * uses a conditional write to reject stale updates (HTTP 409).
 *
 * @param config - The full ConfigDocument to save (must include version)
 * @returns The updated ConfigDocument (version incremented by backend)
 * @throws ConflictError if the version is stale (409 Conflict)
 */
export async function updateConfig(config: ConfigDocument): Promise<ConfigDocument> {
  try {
    const response = await apiClient.put<ConfigDocument>('/config', config);
    return response.data;
  } catch (error: any) {
    // Intercept 409 before the global response interceptor re-throws
    if (error?.response?.status === 409) {
      throw new ConflictError(
        error.response.data?.message ||
          'Configuration was modified by another user. Please reload and try again.'
      );
    }
    throw error;
  }
}

/**
 * Test an S3 bucket connection via STS AssumeRole + ListObjectsV2.
 *
 * @param params - S3 connection parameters (bucket, roleArn, region)
 * @returns TestResult with success flag and message
 */
export async function testS3Connection(params: S3TestParams): Promise<TestResult> {
  const response = await apiClient.post<TestResult>('/config/test-s3', params);
  return response.data;
}

/**
 * Test an SMB destination connection using the referenced credential set.
 *
 * @param params - SMB connection parameters (uncPath, credentialSetName)
 * @returns TestResult with success flag and message
 */
export async function testSMBConnection(params: SMBTestParams): Promise<TestResult> {
  const response = await apiClient.post<TestResult>('/config/test-smb', params);
  return response.data;
}

/**
 * Test a SharePoint connection using the referenced credential set.
 *
 * @param params - SharePoint connection parameters (credentialSetName)
 * @returns TestResult with success flag and message
 */
export async function testSharePointConnection(params: SharePointTestParams): Promise<TestResult> {
  const response = await apiClient.post<TestResult>('/config/test-sharepoint', params);
  return response.data;
}

/**
 * Get the configuration audit log (last 100 entries, newest first).
 *
 * @returns Array of AuditEntry sorted by timestamp descending
 */
export async function getAuditLog(): Promise<AuditEntry[]> {
  const response = await apiClient.get<AuditEntry[]>('/config/audit');
  return response.data;
}

/**
 * Trigger ALL STOP — pause all presses immediately.
 *
 * @returns The updated ConfigDocument after all presses are paused
 */
export async function allStop(): Promise<ConfigDocument> {
  const response = await apiClient.post<ConfigDocument>('/config/all-stop');
  return response.data;
}
